/**
 * A view, portable: what a copied link, a saved view and a review comment carry.
 *
 * The app keeps a view in two homes (ui/urlState.ts): the query string for anything
 * that names nobody, and the history entry for anything that names a person. A link
 * can only carry the first. To carry the second, every name in it is swapped for the
 * person's opaque key (state/personKeys.ts) — `~0a1b2c3d4e~` in place of "Doe, Jane",
 * wherever it sits: a bare value, a list, inside a JSON blob, inside a label such as
 * "team of Doe, Jane". The receiving browser swaps keys back for the people IT is
 * served; a key it cannot resolve is someone outside its access, and is left out.
 *
 *   SharedView v1 = {
 *     v: 1,
 *     path:   '/employees',
 *     search: '?f.dep=HMO&sort=cpc.desc',  // name-free, the URL as it was
 *     pf?:    { supervisor?, employee?, employeeHere?, slice? }  // the bar's people, keyed
 *     view?:  { [key]: raw }                        // the page's personal state, keyed
 *     adapted?: { [key]: raw }                      // the same, through a page adapter
 *   }
 *
 * A slice picked on the Overview carries the bar it came from (`match`), so it goes as
 * the bar, never as its people: the receiver recomputes who works it from THEIR crosstab
 * (review #232: a slice of 555 names inside one comment).
 *
 * `adapted` is for page state that is not names but is not portable either: Claim and
 * Touch detail keep their dropdowns as indexes into a dictionary the API builds per
 * caller, so index 12 is a different facility for someone else. Those pages register an
 * adapter (`share` in useUrlState) that turns indexes into values on the way out and
 * back into the receiver's indexes on the way in, once the rows are loaded.
 *
 * Version 1 is the only version. A later shape bumps `v`; this reader keeps reading 1.
 */
import type { Filters } from '@/data/filters'
import type { CrosstabDict } from '@/data/types'
import type { SliceMatch } from '@/measures/trend'
import { sameValue } from '@/data/blankValue'
import type { PeopleKeys } from './personKeys'

export const SHARED_VERSION = 1

/**
 * The filter bar's person parts, as the history entry holds them (names).
 *
 * `employeeHere`: the Employee filter was picked on THIS entry. Supervisors and the
 * Scorecard apply Employee only then; one carried in from another page is shown and not
 * applied (`EMPLOYEE_PAGE_SCOPED`, data/filters.tsx). Names nobody, so a link carries it.
 */
export type PersonFilters = Partial<Pick<Filters, 'supervisor' | 'employee' | 'slice'>> & {
  employeeHere?: true
}

export interface SharedSlice {
  label: string
  match?: SliceMatch
  /** Keys; only for a slice without a `match` (none is made today, but old ones are). */
  employees?: string[]
}

export interface SharedView {
  v: 1
  path: string
  search: string
  pf?: { supervisor?: string; employee?: string; employeeHere?: true; slice?: SharedSlice }
  view?: Record<string, string>
  adapted?: Record<string, string>
}

/** Query parameters that are instructions to the app, not part of a view. */
export const TRANSIENT_PARAMS = ['v', 'x', 'comment', 'asana', 'mockGraph'] as const

/** What a key reads as where its person is outside the receiver's access. */
export const OUTSIDE = 'someone outside your access'

const TOKEN = /~([0-9a-z]{10})~/g
const WHOLE_TOKEN = /^~([0-9a-z]{10})~$/

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/** Builds the name matcher once per set of keys. */
const matchers = new WeakMap<PeopleKeys, RegExp | null>()
function nameMatcher(keys: PeopleKeys): RegExp | null {
  if (!matchers.has(keys)) {
    // Longest first, so "Lee, Anna" is not read as "Lee, Ann" plus an "a".
    matchers.set(keys, keys.names.length ? new RegExp(keys.names.map(escapeRe).join('|'), 'g') : null)
  }
  return matchers.get(keys)!
}

/** Every name in `text` swapped for its person's key. */
export function tokenize(text: string, keys: PeopleKeys): string {
  const re = nameMatcher(keys)
  if (!re) return text
  return text.replace(re, (name) => {
    const k = keys.toKey(name)
    return k ? `~${k}~` : name
  })
}

/**
 * Keys swapped back for names. A value that is nothing BUT a key nobody here has comes
 * back null — the caller drops it rather than selecting someone it cannot name. A key
 * inside a longer value (a label, a list, JSON) reads as OUTSIDE, which matches nobody.
 */
export function detokenize(text: string, keys: PeopleKeys): { text: string | null; refused: number } {
  const whole = WHOLE_TOKEN.exec(text)
  if (whole) {
    const name = keys.toName(whole[1]!)
    return name ? { text: name, refused: 0 } : { text: null, refused: 1 }
  }
  let refused = 0
  const out = text.replace(TOKEN, (_, k: string) => {
    const name = keys.toName(k)
    if (name) return name
    refused++
    return OUTSIDE
  })
  return { text: out, refused }
}

/** A query string without the app's one-shot instructions, '' or '?…'. */
export function viewSearch(search: string): string {
  const p = new URLSearchParams(search)
  for (const k of TRANSIENT_PARAMS) p.delete(k)
  const s = p.toString()
  return s ? `?${s}` : ''
}

/**
 * The people who work a slice, in a crosstab: anyone with a row inside it. The same
 * test `sliceWeights` (measures/trend.ts) weighs them by, blanks as one "Unknown".
 */
export function slicePeopleFor(ct: CrosstabDict, m: SliceMatch): string[] {
  const out = new Set<string>()
  for (const row of ct.rows) {
    const [e, dept, state, payer, group, fac] = row
    if (m.department && !sameValue(ct.departments[dept], m.department)) continue
    if (m.state && !sameValue(ct.states[state], m.state)) continue
    if (m.payer && !sameValue(ct.payers[payer], m.payer)) continue
    if (m.facilityGroup && !sameValue(ct.facilityGroups[group], m.facilityGroup)) continue
    if (m.facility && !sameValue(ct.facilities[fac], m.facility)) continue
    const name = ct.employees[e]
    if (name) out.add(name)
  }
  return [...out]
}

/* -------------------------------------------------------------------- capture */

/** What a mounted page says about one of its personal keys; see useUrlState's `share`. */
export type Sharer =
  | { kind: 'exclude' }
  /** Index → value on the way out; null leaves the key out. */
  | { kind: 'adapt'; out: (raw: string) => string | null }

export interface Capturable {
  pathname: string
  search: string
  /** The history entry's router state. */
  state: unknown
}

/** The history entry's own parts, as ui/urlState.ts and data/filters.tsx keep them. */
export const VIEW_KEY = 'view'
export const PF_KEY = 'pf'
export const INCOMING_KEY = 'incoming'

/**
 * The view on screen, portable. Every name in it becomes a key; a personal value whose
 * page has excluded it from sharing (free-text search: it can hold anything) is left
 * out; one with an adapter goes through it.
 */
export function captureView(
  at: Capturable,
  keys: PeopleKeys,
  sharers: ReadonlyMap<string, Sharer> = new Map(),
): SharedView {
  const state = (at.state && typeof at.state === 'object' ? at.state : {}) as Record<string, unknown>
  const out: SharedView = { v: 1, path: at.pathname, search: viewSearch(at.search) }

  const pf = state[PF_KEY] as PersonFilters | undefined
  if (pf) {
    const p: NonNullable<SharedView['pf']> = {}
    if (pf.supervisor) p.supervisor = tokenize(pf.supervisor, keys)
    if (pf.employee) p.employee = tokenize(pf.employee, keys)
    if (pf.employee && pf.employeeHere === true) p.employeeHere = true
    if (pf.slice) {
      p.slice = { label: tokenize(pf.slice.label, keys) }
      if (pf.slice.match) p.slice.match = pf.slice.match
      else p.slice.employees = pf.slice.employees.map((n) => tokenize(n, keys))
    }
    if (Object.keys(p).length) out.pf = p
  }

  const view = state[VIEW_KEY] as Record<string, string> | undefined
  for (const [k, raw] of Object.entries(view ?? {})) {
    if (typeof raw !== 'string') continue
    const s = sharers.get(k)
    if (s?.kind === 'exclude') continue
    if (s?.kind === 'adapt') {
      const v = s.out(raw)
      if (v !== null) (out.adapted ??= {})[k] = tokenize(v, keys)
    } else (out.view ??= {})[k] = tokenize(raw, keys)
  }
  return out
}

/** True when the view names nobody and has no page state: a plain URL carries it all. */
export const isPlain = (v: SharedView) => !v.pf && !v.view && !v.adapted

/* -------------------------------------------------------------------- resolve */

export interface ResolvedView {
  path: string
  search: string
  /** The router state to open it with. */
  state: { [PF_KEY]: PersonFilters; [VIEW_KEY]: Record<string, string>; [INCOMING_KEY]?: Record<string, string> }
  /** How many people in it are outside the receiver's access (left out). */
  refused: number
}

/**
 * A portable view, for this receiver: keys back to names, where they have them. The
 * slice's people come from the receiver's own crosstab when the slice carries its bar.
 */
export function resolveView(v: SharedView, keys: PeopleKeys, crosstab: CrosstabDict | null): ResolvedView {
  let refused = 0
  const name = (s: string | undefined) => {
    if (!s) return null
    const r = detokenize(s, keys)
    refused += r.refused
    return r.text
  }

  const pf: PersonFilters = {}
  const sup = name(v.pf?.supervisor)
  if (sup) pf.supervisor = sup
  const emp = name(v.pf?.employee)
  if (emp) pf.employee = emp
  if (emp && v.pf?.employeeHere === true) pf.employeeHere = true
  const sl = v.pf?.slice
  if (sl) {
    const label = name(sl.label) ?? ''
    let employees: string[]
    if (sl.match) employees = crosstab ? slicePeopleFor(crosstab, sl.match) : []
    else {
      employees = []
      for (const k of sl.employees ?? []) {
        const n = name(k)
        if (n) employees.push(n)
      }
    }
    pf.slice = sl.match ? { label, employees, match: sl.match } : { label, employees }
  }

  const view: Record<string, string> = {}
  for (const [k, raw] of Object.entries(v.view ?? {})) {
    const t = name(raw)
    if (t !== null) view[k] = t
  }
  let incoming: Record<string, string> | undefined
  for (const [k, raw] of Object.entries(v.adapted ?? {})) {
    const t = name(raw)
    if (t !== null) (incoming ??= {})[k] = t
  }

  return {
    path: v.path,
    search: viewSearch(v.search),
    state: { [PF_KEY]: pf, [VIEW_KEY]: view, ...(incoming ? { [INCOMING_KEY]: incoming } : {}) },
    refused,
  }
}

/** A parsed, checked SharedView, or null. Anything from a link or the server is untrusted. */
export function parseSharedView(x: unknown): SharedView | null {
  if (!x || typeof x !== 'object') return null
  const o = x as Record<string, unknown>
  if (o.v !== 1 || typeof o.path !== 'string' || !o.path.startsWith('/') || o.path.startsWith('//')) return null
  const strMap = (m: unknown) =>
    m && typeof m === 'object' && Object.values(m).every((s) => typeof s === 'string')
      ? (m as Record<string, string>)
      : undefined
  const out: SharedView = { v: 1, path: o.path, search: typeof o.search === 'string' ? o.search : '' }
  if (o.pf && typeof o.pf === 'object') {
    const p = o.pf as Record<string, unknown>
    const pf: NonNullable<SharedView['pf']> = {}
    if (typeof p.supervisor === 'string') pf.supervisor = p.supervisor
    if (typeof p.employee === 'string') pf.employee = p.employee
    if (p.employeeHere === true) pf.employeeHere = true
    const s = p.slice as Record<string, unknown> | undefined
    if (s && typeof s === 'object' && typeof s.label === 'string') {
      pf.slice = { label: s.label }
      if (s.match && typeof s.match === 'object') pf.slice.match = s.match as SliceMatch
      if (Array.isArray(s.employees)) pf.slice.employees = s.employees.filter((e): e is string => typeof e === 'string')
    }
    out.pf = pf
  }
  const view = strMap(o.view)
  if (view) out.view = view
  const adapted = strMap(o.adapted)
  if (adapted) out.adapted = adapted
  return out
}

/* -------------------------------------------------------------------- inline */

/** Base64url of UTF-8 JSON: the `x=` of a link small enough to carry its own state. */
export function encodeInline(v: unknown): string {
  const bytes = new TextEncoder().encode(JSON.stringify(v))
  let bin = ''
  for (const b of bytes) bin += String.fromCharCode(b)
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export function decodeInline(s: string): unknown {
  try {
    const bin = atob(s.replace(/-/g, '+').replace(/_/g, '/'))
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0))
    return JSON.parse(new TextDecoder().decode(bytes))
  } catch {
    return null
  }
}

/** Above this many characters of `x=`, the state goes to a saved view instead. */
export const INLINE_LIMIT = 400

/** A saved view's id: 10 base-62 characters, the first a letter, so never read as `v=1`. */
export const VIEW_ID = /^[A-Za-z][A-Za-z0-9]{9}$/

/**
 * The link for a view. A plain view is its own URL plus `v=1`, which marks the filters
 * in it as the whole truth (a bare URL opens with the reader's remembered filters). A
 * view with people or page state carries it as `x=`, when that is short, or is saved
 * (`save`) and goes as `v=<id>`. `save` failing falls back to the long inline link.
 */
export async function linkFor(
  view: SharedView,
  origin: string,
  save: (v: SharedView) => Promise<string>,
): Promise<{ url: string; saved: boolean; long: boolean }> {
  const params = new URLSearchParams(view.search)
  if (isPlain(view)) {
    params.set('v', String(SHARED_VERSION))
    return { url: `${origin}${view.path}?${params}`, saved: false, long: false }
  }
  const { v: _v, path: _p, search: _s, ...rest } = view
  const x = encodeInline(rest)
  if (x.length <= INLINE_LIMIT) {
    params.set('v', String(SHARED_VERSION))
    params.set('x', x)
    return { url: `${origin}${view.path}?${params}`, saved: false, long: false }
  }
  try {
    const id = await save(view)
    return { url: `${origin}${view.path}?v=${id}`, saved: true, long: false }
  } catch {
    params.set('v', String(SHARED_VERSION))
    params.set('x', x)
    return { url: `${origin}${view.path}?${params}`, saved: false, long: true }
  }
}

/** What a URL asks the app to open, if anything. */
export type Incoming =
  | { kind: 'saved'; id: string }
  | { kind: 'inline'; view: SharedView }
  | { kind: 'plain' }
  | null

export function readIncoming(pathname: string, search: string): Incoming {
  const p = new URLSearchParams(search)
  const v = p.get('v')
  if (v === null) return null
  if (VIEW_ID.test(v)) return { kind: 'saved', id: v }
  if (v !== String(SHARED_VERSION)) return null
  const x = p.get('x')
  if (x === null) return { kind: 'plain' }
  const rest = decodeInline(x)
  if (!rest || typeof rest !== 'object') return { kind: 'plain' }
  const view = parseSharedView({ ...rest, v: 1, path: pathname, search })
  return view ? { kind: 'inline', view } : { kind: 'plain' }
}
